<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageFormatter from 'shared/helpers/MessageFormatter';

import NextButton from 'dashboard/components-next/button/Button.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';

export default {
  name: 'AceleresCreateCannedModal',
  components: {
    NextButton,
    WootMessageEditor,
    AudioRecorder,
  },
  props: {
    show: { type: Boolean, default: false },
    editing: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  setup() {
    const { t, te } = useI18n();
    return { v$: useVuelidate(), t, te };
  },
  data() {
    return {
      shortCode: '',
      content: '',
      attachments: [],
      uploading: false,
      uploadProgress: { current: 0, total: 0, percent: 0 },
      recording: false,
      recordingPaused: false,
      audioFormat: AUDIO_FORMATS.MP3,
      sheetDragStartY: null,
      sheetDragStartHeight: 0,
      sheetHeight: null,
      modal: {
        showLoading: false,
      },
    };
  },
  validations() {
    return {
      shortCode: {
        required,
        minLength: minLength(2),
      },
      content: {},
    };
  },
  computed: {
    hasAttachment() {
      return this.attachments.length > 0;
    },
    hasSingleCaptionedImage() {
      return (
        this.attachments.length === 1 &&
        this.attachmentKind(this.attachments[0]) === 'image' &&
        Boolean(this.previewContent)
      );
    },
    isFormValid() {
      return (
        !this.v$.shortCode.$invalid &&
        (this.content.trim().length > 0 || this.hasAttachment)
      );
    },
    isEditing() {
      return !!(this.editing && this.editing.id);
    },
    modalDescription() {
      if (this.isEditing && this.te('CANNED_MGMT.EDIT.DESC')) {
        return this.t('CANNED_MGMT.EDIT.DESC');
      }
      return this.t('CANNED_MGMT.VIEW.DESCRIPTION');
    },
    previewContent() {
      return new MessageFormatter(this.content, false, false, true)
        .formattedMessage;
    },
    previewShortcut() {
      return `/${this.shortCode.replace(/^\/+/, '')}`;
    },
    uploadProgressPercentLabel() {
      return `${this.uploadProgress.percent}%`;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.syncFromEditing();
      }
    },
    editing: {
      deep: true,
      handler() {
        if (this.show) this.syncFromEditing();
      },
    },
  },
  mounted() {
    if (this.show) this.syncFromEditing();
  },
  methods: {
    closeOnBackdrop() {
      if (window.matchMedia('(max-width: 767px)').matches) {
        this.$emit('close');
      }
    },
    startSheetDrag(event) {
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      if (event.target.closest('button')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.sheetDragStartY = event.clientY;
      this.sheetDragStartHeight =
        event.currentTarget.parentElement.offsetHeight;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    moveSheetDrag(event) {
      if (this.sheetDragStartY === null) return;
      const distance = event.clientY - this.sheetDragStartY;
      this.sheetHeight = Math.max(
        0,
        Math.min(this.sheetDragStartHeight - distance, window.innerHeight - 12)
      );
    },
    endSheetDrag(event) {
      if (this.sheetDragStartY === null) return;
      if (
        event.clientY > this.sheetDragStartY &&
        event.clientY >= window.innerHeight - 48
      ) {
        this.$emit('close');
      }
      this.sheetDragStartY = null;
    },
    cancelSheetDrag() {
      if (this.sheetDragStartY !== null) {
        this.sheetHeight = this.sheetDragStartHeight;
      }
      this.sheetDragStartY = null;
    },
    syncFromEditing() {
      this.cancelSheetDrag();
      this.sheetHeight = null;
      this.shortCode = this.editing?.short_code || '';
      this.content = this.editing?.content || '';
      this.recording = false;
      this.recordingPaused = false;
      this.uploadProgress = { current: 0, total: 0, percent: 0 };
      this.attachments = this.buildAttachmentState(this.editing);
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [];
      this.recording = false;
      this.recordingPaused = false;
      this.uploadProgress = { current: 0, total: 0, percent: 0 };
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    buildAttachmentState(source) {
      const attachments = Array.isArray(source?.attachments)
        ? source.attachments
        : [];
      if (attachments.length) {
        return attachments
          .filter(item => item?.attachment_url && item?.attachment_signed_id)
          .map((item, index) => this.normalizeAttachment(item, index));
      }
      if (source?.attachment_url && source?.attachment_signed_id) {
        return [this.normalizeAttachment(source, 0)];
      }
      return [];
    },
    normalizeAttachment(item, index = 0) {
      const attachmentUrl = item.attachment_url || item.attachmentUrl;
      const attachmentSignedId =
        item.attachment_signed_id || item.attachmentSignedId;
      const fileName =
        item.file_name ||
        item.fileName ||
        decodeURIComponent(
          attachmentUrl.toString().split('?')[0].split('/').pop()
        );
      const fileType = item.file_type || item.fileType || '';

      return {
        attachment_url: attachmentUrl,
        attachment_signed_id: attachmentSignedId,
        file_name: fileName,
        file_type: fileType,
        position: item.position ?? index,
      };
    },
    attachmentKind(attachment) {
      const type = attachment.file_type || '';
      const url = attachment.attachment_url || '';
      if (
        type.startsWith('image/') ||
        /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url)
      )
        return 'image';
      if (
        type.startsWith('audio/') ||
        /\.(mp3|wav|ogg|m4a|aac)(\?|$)/i.test(url)
      )
        return 'audio';
      if (
        type.startsWith('video/') ||
        /\.(mp4|mov|avi|wmv|webm|mkv)(\?|$)/i.test(url)
      )
        return 'video';
      return 'file';
    },
    async uploadBlob(file, name, type, onUploadProgress) {
      const formData = new FormData();
      formData.append('attachment', file);
      const accountId = this.$store.getters.getCurrentAccountId;
      const response = await axios.post(
        `/api/v1/accounts/${accountId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress,
        }
      );
      this.attachments.push({
        attachment_url: response.data.file_url,
        attachment_signed_id: response.data.blob_id,
        file_name: name || file.name,
        file_type: type || file.type || '',
        position: this.attachments.length,
      });
    },
    async handleFileSelect(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      this.uploading = true;
      const totalBytes = files.reduce(
        (total, file) => total + Math.max(file.size || 0, 1),
        0
      );
      let completedBytes = 0;
      this.uploadProgress = { current: 1, total: files.length, percent: 0 };
      try {
        await files.reduce(
          (promise, file, index) =>
            promise.then(async () => {
              const fileBytes = Math.max(file.size || 0, 1);
              this.uploadProgress.current = index + 1;
              await this.uploadBlob(file, undefined, undefined, progress => {
                const loadedBytes = Math.min(progress.loaded || 0, fileBytes);
                this.uploadProgress.percent = Math.round(
                  ((completedBytes + loadedBytes) / totalBytes) * 100
                );
              });
              completedBytes += fileBytes;
              this.uploadProgress.percent = Math.round(
                (completedBytes / totalBytes) * 100
              );
            }),
          Promise.resolve()
        );
      } catch (e) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENT.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
        event.target.value = '';
      }
    },
    startRecording() {
      this.recording = true;
      this.recordingPaused = false;
    },
    stopRecording() {
      if (this.$refs.cannedAudioRecorder)
        this.$refs.cannedAudioRecorder.stopRecording();
    },
    cancelRecording() {
      this.recording = false;
      this.recordingPaused = false;
    },
    toggleRecordingPlayPause() {
      if (!this.$refs.cannedAudioRecorder) return;

      if (this.recordingPaused) {
        this.$refs.cannedAudioRecorder.resumeRecording();
      } else {
        this.$refs.cannedAudioRecorder.pauseRecording();
      }
    },
    onRecordingPaused() {
      this.recordingPaused = true;
    },
    onRecordingResumed() {
      this.recordingPaused = false;
    },
    onRecordingError(message) {
      this.recording = false;
      this.recordingPaused = false;
      this.uploading = false;
      useAlert(message || 'Não foi possível processar o áudio gravado.');
    },
    async onAudioRecorded(file) {
      this.recordingPaused = false;
      if (!file?.file) {
        this.recording = false;
        return;
      }

      this.uploading = true;
      this.uploadProgress = { current: 1, total: 1, percent: 0 };
      try {
        await this.uploadBlob(
          file.file,
          `gravacao-${Date.now()}.mp3`,
          'audio/mp3',
          progress => {
            const total = progress.total || file.file.size || 1;
            this.uploadProgress.percent = Math.round(
              ((progress.loaded || 0) / total) * 100
            );
          }
        );
        this.uploadProgress.percent = 100;
      } catch (e) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENT.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
        this.recording = false;
      }
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1);
      this.attachments = this.attachments.map((attachment, position) => ({
        ...attachment,
        position,
      }));
    },
    async saveCannedResponse() {
      this.modal.showLoading = true;
      const payload = {
        short_code: this.shortCode,
        content: this.content,
        attachments: this.attachments.map((attachment, position) => ({
          attachment_url: attachment.attachment_url,
          attachment_signed_id: attachment.attachment_signed_id,
          file_name: attachment.file_name,
          file_type: attachment.file_type,
          position,
        })),
      };

      try {
        if (this.isEditing) {
          await this.$store.dispatch('updateCannedResponse', {
            id: this.editing.id,
            ...payload,
          });
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        } else {
          await this.$store.dispatch('createCannedResponse', payload);
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
        }
        this.$emit('saved');
        this.$emit('close');
        this.resetForm();
      } catch (error) {
        const errorMessage =
          error?.message ||
          (this.isEditing
            ? this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE')
            : this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE'));
        useAlert(errorMessage);
      } finally {
        this.modal.showLoading = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="acl-canned-sheet-overlay fixed inset-0 z-[2147483600] grid min-h-dvh place-items-center overflow-y-auto bg-black/35 p-4"
      role="presentation"
      @click.self="closeOnBackdrop"
      @keydown.esc="$emit('close')"
    >
      <section
        class="acl-canned-sheet flex max-h-[calc(100dvh-2rem)] w-full max-w-[860px] flex-col overflow-hidden rounded-[20px] border border-n-weak bg-n-background shadow-[0_30px_90px_rgba(15,23,42,0.45)]"
        :style="{
          '--acl-canned-sheet-height':
            sheetHeight === null ? null : `${sheetHeight}px`,
        }"
        role="dialog"
        aria-modal="true"
        :aria-label="
          isEditing
            ? $t('CANNED_MGMT.EDIT.TITLE')
            : $t('CANNED_MGMT.VIEW.TITLE')
        "
      >
        <form
          class="flex min-h-0 flex-1 flex-col overflow-hidden md:overflow-hidden"
          @submit.prevent="saveCannedResponse()"
        >
          <header
            class="acl-canned-sheet-header flex flex-shrink-0 items-start gap-3 border-b border-n-weak px-6 py-5"
            @pointerdown="startSheetDrag"
            @pointermove="moveSheetDrag"
            @pointerup="endSheetDrag"
            @pointercancel="cancelSheetDrag"
          >
            <span class="acl-canned-sheet-handle" aria-hidden="true" />
            <span
              class="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <i class="i-lucide-zap size-5" />
            </span>
            <div class="min-w-0 flex-1">
              <h2 class="m-0 text-lg font-semibold leading-6 text-n-slate-12">
                {{
                  isEditing
                    ? $t('CANNED_MGMT.EDIT.TITLE')
                    : $t('CANNED_MGMT.VIEW.TITLE')
                }}
              </h2>
              <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-10">
                {{ modalDescription }}
              </p>
            </div>
            <button
              type="button"
              class="hidden size-9 flex-shrink-0 items-center justify-center rounded-lg border-0 bg-n-solid-1 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 md:inline-flex"
              :aria-label="$t('GENERAL.CLOSE')"
              :title="$t('GENERAL.CLOSE')"
              @click="$emit('close')"
            >
              <i class="i-lucide-x size-5" />
            </button>
          </header>

          <div
            class="acl-canned-sheet-content grid min-h-0 flex-1 overflow-y-auto md:overflow-y-auto md:grid-cols-[minmax(0,494px)_minmax(0,366px)]"
          >
            <div class="min-w-0 space-y-4 px-6 py-5">
              <div>
                <label
                  class="mb-2 block text-sm font-medium text-n-slate-12"
                  :class="{ error: v$.shortCode.$error }"
                >
                  {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
                </label>
                <div class="relative">
                  <span
                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center w-8 justify-center rounded-l-lg bg-n-brand/5 text-sm font-semibold text-n-brand"
                    aria-hidden="true"
                  >
                    {{ previewShortcut.charAt(0) }}
                  </span>
                  <input
                    v-model="shortCode"
                    autofocus
                    type="text"
                    class="reset-base h-11 w-full rounded-lg border border-n-strong bg-n-background py-2 pl-11 pr-3 text-sm text-n-slate-12 outline-none transition focus:border-n-brand focus:ring-2 focus:ring-n-brand/15"
                    :placeholder="
                      $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')
                    "
                    @blur="v$.shortCode.$touch"
                  />
                </div>
                <p class="mb-0 mt-1 text-xs text-n-slate-10">
                  {{ $t('CANNED_MGMT.VIEW.SHORTCUT_HELP') }}
                </p>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-n-slate-12">
                  {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
                </label>
                <div
                  class="acl-canned-editor-wrap overflow-hidden rounded-xl border border-n-weak bg-n-background focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/15"
                >
                  <WootMessageEditor
                    v-model="content"
                    class="message-editor border-0 [&>div]:px-1"
                    channel-type="Context::Default"
                    override-line-breaks
                    list-from-current-line
                    enable-variables
                    :enable-canned-responses="false"
                    :placeholder="
                      $t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')
                    "
                  />
                </div>
              </div>

              <div>
                <div class="mb-2 flex items-center justify-between">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('CANNED_MGMT.ATTACHMENT.LABEL') }}
                  </label>
                  <span class="text-xs text-n-slate-9">
                    {{
                      $t('CANNED_MGMT.VIEW.ATTACHMENT_COUNT', {
                        n: attachments.length,
                      })
                    }}
                  </span>
                </div>

                <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label
                    class="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-n-strong px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
                  >
                    <span
                      v-if="uploading"
                      class="i-lucide-loader-2 size-4 animate-spin"
                    />
                    <span v-else class="i-lucide-paperclip size-4" />
                    <span>
                      {{
                        uploading
                          ? $t('CANNED_MGMT.ATTACHMENT.UPLOADING')
                          : $t('CANNED_MGMT.VIEW.ADD_FILES')
                      }}
                    </span>
                    <input
                      type="file"
                      multiple
                      class="hidden"
                      accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip,.rar,.json,.xml"
                      :disabled="uploading"
                      @change="handleFileSelect"
                    />
                  </label>
                  <button
                    v-if="!recording"
                    type="button"
                    class="flex min-h-11 items-center justify-center gap-2 rounded-lg border border-dashed border-n-strong px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
                    @click="startRecording"
                  >
                    <span class="i-lucide-mic size-4" />
                    <span>{{ $t('CANNED_MGMT.ATTACHMENT.RECORD_AUDIO') }}</span>
                  </button>
                </div>

                <div
                  v-if="hasAttachment"
                  class="mt-2 max-h-56 space-y-2 overflow-y-auto"
                >
                  <div
                    v-for="(attachment, index) in attachments"
                    :key="`${attachment.attachment_signed_id || attachment.attachment_url}-${index}`"
                    :class="
                      attachmentKind(attachment) === 'audio'
                        ? 'acl-canned-attachment--audio acl-canned-audio-recorder rounded-xl border border-n-weak bg-n-alpha-1 p-3'
                        : 'flex items-center gap-3 rounded-xl bg-n-solid-1 p-2'
                    "
                  >
                    <img
                      v-if="attachmentKind(attachment) === 'image'"
                      :src="attachment.attachment_url"
                      class="size-12 flex-shrink-0 rounded-lg object-cover"
                      :alt="attachment.file_name"
                    />
                    <AudioRecorder
                      v-else-if="attachmentKind(attachment) === 'audio'"
                      class="[&_.acl-wa-icon-btn:first-child]:!bg-[#00a884] [&_.acl-wa-icon-btn:first-child]:!text-white [&_.acl-wa-icon-btn:first-child:hover]:!bg-[#069373] [&_.acl-wa-icon-btn:first-child:hover]:!text-white"
                      :audio-record-format="audioFormat"
                      :playback-src="attachment.attachment_url"
                      :title="attachment.file_name"
                      @delete="removeAttachment(index)"
                    />
                    <div
                      v-else-if="attachmentKind(attachment) === 'video'"
                      class="flex flex-col gap-2 flex-1 min-w-0"
                    >
                      <span
                        class="i-lucide-video text-n-slate-11 flex-shrink-0 size-5"
                      />
                      <span class="text-sm text-n-slate-12 truncate">
                        {{ attachment.file_name }}
                      </span>
                    </div>
                    <div v-else class="flex items-center gap-2 flex-1 min-w-0">
                      <span
                        class="i-lucide-file text-n-slate-11 flex-shrink-0 size-5"
                      />
                      <span class="text-sm text-n-slate-12 truncate">
                        {{ attachment.file_name }}
                      </span>
                    </div>
                    <video
                      v-if="attachmentKind(attachment) === 'video'"
                      :src="attachment.attachment_url"
                      controls
                      class="max-h-40 max-w-xs flex-1 rounded-lg"
                    />
                    <button
                      v-if="attachmentKind(attachment) !== 'audio'"
                      type="button"
                      class="acl-canned-attachment__remove ml-auto inline-flex size-8 flex-shrink-0 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
                      :aria-label="$t('GENERAL.DISCARD')"
                      :title="$t('GENERAL.DISCARD')"
                      @click="removeAttachment(index)"
                    >
                      <span class="i-lucide-x size-4" />
                    </button>
                  </div>
                </div>

                <div
                  v-if="uploading && uploadProgress.total"
                  class="mt-3 rounded-xl bg-n-alpha-1 p-3"
                  role="progressbar"
                  :aria-label="$t('CANNED_MGMT.ATTACHMENT.UPLOADING')"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :aria-valuenow="uploadProgress.percent"
                >
                  <div
                    class="mb-1 flex items-center justify-between text-xs text-n-slate-10"
                  >
                    <span>
                      {{
                        $t('CANNED_MGMT.ATTACHMENT.PROGRESS', {
                          current: uploadProgress.current,
                          total: uploadProgress.total,
                        })
                      }}
                    </span>
                    <span class="tabular-nums">
                      {{ uploadProgressPercentLabel }}
                    </span>
                  </div>
                  <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-3">
                    <div
                      class="h-full rounded-full bg-n-brand transition-[width] duration-200"
                      :style="{ width: `${uploadProgress.percent}%` }"
                    />
                  </div>
                </div>

                <div
                  v-if="recording"
                  class="acl-canned-audio-recorder mt-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                >
                  <AudioRecorder
                    ref="cannedAudioRecorder"
                    class="[&_.acl-wa-icon-btn:first-child]:!bg-[#00a884] [&_.acl-wa-icon-btn:first-child]:!text-white [&_.acl-wa-icon-btn:first-child:hover]:!bg-[#069373] [&_.acl-wa-icon-btn:first-child:hover]:!text-white"
                    :audio-record-format="audioFormat"
                    :is-paused="recordingPaused"
                    @delete="cancelRecording"
                    @playpause="toggleRecordingPlayPause"
                    @pause="onRecordingPaused"
                    @play="onRecordingResumed"
                    @record-error="onRecordingError"
                    @send="stopRecording"
                    @finish-record="onAudioRecorded"
                  />
                </div>
              </div>
            </div>
            <aside
              class="flex min-w-0 flex-col border-t border-n-weak bg-n-solid-1/40 px-6 py-5 md:border-l md:border-t-0"
            >
              <h3
                class="mb-3 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
              >
                {{ $t('CANNED_MGMT.VIEW.PREVIEW_TITLE') }}
              </h3>
              <div
                class="flex min-h-72 flex-1 flex-col justify-end gap-2 rounded-[14px] bg-[#efeae2] p-4 md:min-h-[436px]"
              >
                <div
                  v-if="previewContent && !hasSingleCaptionedImage"
                  v-dompurify-html="previewContent"
                  class="acl-canned-preview-bubble prose prose-bubble mb-0 break-words rounded-xl rounded-tr-sm bg-[#d9fdd3] px-3 py-2 text-sm leading-5 text-[#25292d]"
                />
                <p
                  v-else-if="!hasAttachment"
                  class="my-auto text-center text-sm text-[#7d8389]"
                >
                  {{ $t('CANNED_MGMT.VIEW.PREVIEW_EMPTY') }}
                </p>
                <div
                  v-for="(attachment, index) in attachments"
                  :key="`preview-${index}`"
                  class="acl-canned-preview-bubble rounded-xl bg-[#d9fdd3] px-3 py-2 text-sm text-[#25292d]"
                  :class="[
                    attachmentKind(attachment) === 'audio'
                      ? 'acl-canned-preview-bubble--audio acl-canned-audio-recorder ml-auto w-full max-w-full'
                      : 'ml-auto max-w-full',
                  ]"
                >
                  <img
                    v-if="attachmentKind(attachment) === 'image'"
                    :src="attachment.attachment_url"
                    :alt="attachment.file_name"
                    class="max-h-40 rounded-lg object-contain"
                  />
                  <AudioRecorder
                    v-else-if="attachmentKind(attachment) === 'audio'"
                    class="acl-canned-preview-audio"
                    :audio-record-format="audioFormat"
                    :playback-src="attachment.attachment_url"
                    read-only
                  />
                  <video
                    v-else-if="attachmentKind(attachment) === 'video'"
                    :src="attachment.attachment_url"
                    controls
                    class="max-h-40 max-w-full rounded-lg"
                  />
                  <div v-else class="flex items-center gap-2">
                    <span class="i-lucide-file size-4 shrink-0" /><span
                      class="min-w-0 break-words text-xs"
                    >
                      {{ attachment.file_name }}
                    </span>
                  </div>
                  <div
                    v-if="hasSingleCaptionedImage"
                    v-dompurify-html="previewContent"
                    class="acl-canned-preview-caption prose prose-bubble mb-0 mt-2 break-words text-sm leading-5"
                  />
                </div>
              </div>
              <p class="mb-0 mt-3 text-xs text-n-slate-10">
                {{ $t('CANNED_MGMT.VIEW.PREVIEW_HINT') }}
                <span class="font-mono font-semibold text-[#12813b]">{{
                  previewShortcut
                }}</span>
              </p>
            </aside>
          </div>

          <footer
            class="acl-canned-sheet-footer flex flex-shrink-0 items-center justify-end gap-2 border-t border-n-weak bg-n-background px-6 py-4"
          >
            <NextButton
              variant="outline"
              color="slate"
              type="reset"
              :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
              @click.prevent="$emit('close')"
            />
            <NextButton
              type="submit"
              :label="
                isEditing
                  ? $t('CANNED_MGMT.VIEW.SAVE_EDIT')
                  : $t('CANNED_MGMT.VIEW.SAVE')
              "
              :disabled="!isFormValid || modal.showLoading || uploading"
              :is-loading="modal.showLoading"
            />
          </footer>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.acl-canned-audio-recorder {
  :deep(.acl-wa-recorder) {
    width: 100%;
    padding: 0;
  }

  :deep(.acl-wa-recorder-main) {
    width: 100%;
    margin: 0;
    gap: clamp(0.375rem, 2vw, 0.75rem);
  }

  :deep(.acl-wa-recorder-main > .acl-wa-icon-btn:not(.acl-wa-playpause)) {
    order: 1 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-wa-rec-dot) {
    order: 2 !important;
  }

  :deep(.acl-wa-audio-center > .acl-wa-time) {
    order: 3 !important;
  }

  :deep(.acl-wa-audio-center > .acl-wa-wave) {
    order: 4 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-wa-playpause) {
    order: 5 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-tip-wrapper) {
    order: 6 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-wa-send) {
    order: 7 !important;
  }
}

.acl-canned-editor-wrap {
  ::v-deep(.ProseMirror-woot-style) {
    min-height: 8.25rem;
  }
}

.acl-canned-sheet-handle {
  display: none;
}

@media (max-width: 767px) {
  .acl-canned-sheet-overlay {
    align-items: end;
    justify-items: stretch;
    overflow: hidden;
    padding: 0;
  }

  .acl-canned-sheet {
    width: 100%;
    max-width: none;
    height: var(--acl-canned-sheet-height, 85dvh);
    max-height: calc(100dvh - env(safe-area-inset-top, 0px) - 12px);
    margin: 0;
    border: 0;
    border-radius: 24px 24px 0 0;
    animation: acl-canned-sheet-enter 0.22s ease-out both;
  }

  .acl-canned-sheet-header {
    position: relative;
    display: block;
    padding: 32px 16px 16px;
    text-align: center;
    touch-action: none;
  }

  .acl-canned-sheet-header > span:not(.acl-canned-sheet-handle),
  .acl-canned-sheet-header p {
    display: none;
  }

  .acl-canned-sheet-handle {
    position: absolute;
    top: 10px;
    left: 50%;
    display: block;
    width: 40px;
    height: 5px;
    border-radius: 999px;
    background: #aeb4ba;
    transform: translateX(-50%);
  }

  .acl-canned-sheet-content {
    overscroll-behavior: contain;
  }

  .acl-canned-sheet-content > div,
  .acl-canned-sheet-content > aside {
    padding: 16px;
  }

  .acl-canned-sheet-footer {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  }

  .acl-canned-sheet-footer > * {
    flex: 1 1 0;
  }

  .acl-canned-preview-bubble {
    align-self: flex-end;
    width: fit-content;
    max-width: 85%;
  }

  .acl-canned-sheet-content aside > div {
    min-height: 12rem;
  }

  .acl-canned-sheet-content input[type='text'],
  .acl-canned-editor-wrap,
  .acl-canned-sheet-content label:has(> input[type='file']) {
    border-color: rgb(var(--border-strong));
    border-radius: 0.5rem;
  }

  .acl-canned-sheet-content label:has(> input[type='file']) {
    border-style: solid;
  }

  .acl-canned-audio-recorder {
    padding: 8px;

    :deep(.acl-wa-recorder-main) {
      gap: 4px;
    }

    :deep(.acl-wa-audio-center) {
      display: flex;
      order: 3;
      flex: 1 1 auto;
      min-width: 0;
      align-items: center;
      gap: 4px;
    }

    :deep(.acl-wa-audio-center > .acl-wa-wave) {
      flex: 1 1 auto;
      min-width: 0;
    }

    :deep(.acl-wa-recorder-main > .acl-wa-playpause) {
      order: 4 !important;
      width: 32px;
      height: 32px;
    }

    :deep(.acl-wa-recorder-main > .acl-tip-wrapper) {
      order: 5 !important;
    }

    :deep(.acl-wa-recorder:not(.is-paused) .acl-tip-wrapper) {
      display: none;
    }

    :deep(.acl-wa-recorder-main > .acl-wa-send) {
      order: 6 !important;
      width: 38px;
      height: 38px;
    }

    :deep(.acl-wa-recorder-main > .acl-wa-icon-btn:not(.acl-wa-playpause)) {
      width: 32px;
      height: 32px;
    }
  }

  .acl-canned-editor-wrap {
    // ProseMirror's floating menubar switches to `position: fixed` when the
    // modal form scrolls. Keep it in the editor's layout flow on mobile so the
    // typing area cannot slide underneath it.
    overflow: visible;

    ::v-deep(.ProseMirror-menubar) {
      position: static !important;
      top: auto !important;
      left: auto !important;
      right: auto !important;
      display: flex !important;
      min-height: 0 !important;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      gap: 0 !important;
      width: 100% !important;
      max-width: 100%;
      padding: 0.25rem;
      box-sizing: border-box;
      border-bottom: 1px solid rgb(var(--border-weak));
      background-color: transparent;
    }

    // The floating plugin adds a spacer when it takes the toolbar out of flow.
    // The toolbar stays in flow here, so that spacer would leave an empty gap.
    ::v-deep(.ProseMirror-menubar-spacer) {
      display: none !important;
    }

    ::v-deep(.ProseMirror-menuitem) {
      display: inline-flex;
      flex: 0 1 2rem;
      width: 2rem !important;
      min-width: 0;
      height: 2.25rem !important;
      min-height: 2.25rem;
      align-items: center;
      justify-content: center;
      margin: 0 !important;
      border-radius: 0.5rem;
    }

    ::v-deep(.ProseMirror-menuitem .ProseMirror-icon) {
      display: flex;
      width: 1.25rem !important;
      height: 1.25rem !important;
      align-items: center;
      justify-content: center;
      padding: 0 !important;
    }

    ::v-deep(.ProseMirror-woot-style) {
      min-height: 9rem;
    }
  }
}

@keyframes acl-canned-sheet-enter {
  from {
    translate: 0 100%;
  }

  to {
    translate: 0 0;
  }
}
</style>
